/**
 * PROJECT-093 / P080 — Mailbox (own-mailbox-ON-Raisy) API.
 *
 * The real "Sending Accounts" data source: the user's provisioned mailboxes, fetched through
 * the raisy_BE InboxKit proxy (FE never calls InboxKit directly — P093 D-057).
 *   GET /api/v1/inboxkit/mailboxes  → { mailboxes: [{uid, username, domain_name, platform, status, is_admin}] }
 *
 * P080 D-102: the two per-mailbox TEST actions (test-mailbox-health, send-test-to-external)
 * attach to these rows. No demo rows, no fabricated health/warm-up (rule 0c).
 */
import AuthUtils from "@/providers/firebase/authUtils";
import { ApiResponse } from "./kernelBotApi";
import { readWithRetry, withTimeout, type ReadPolicy } from "./resilientRead";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

/**
 * The budget for the two REAL SENDS, and it is a TIMEOUT and never a retry: a second POST is a second real
 * email. 150 s is generous because the call hands a message to the provider — a timeout means "we stopped
 * waiting", never "it did not send" (F12 challenge `F3`; `warmupTestHealth` sizes its own budget from the
 * wait the user asked for, because a probe still running must not be cut off).
 */
const SEND_TIMEOUT_MS = 150_000;

/**
 * The budget for a LIST read — the same three attempts at 20 s the readiness read uses, because a list
 * and a detail read fail for the same reasons (a slow query, a blip, a cold container).
 *
 * ⚠️ WHY THIS EXISTS AS A NAMED CONSTANT (F12 challenge F11, 2026-10-11). The hardening had bounded the
 * reads it looked at and left the page's PRIMARY LOAD on a bare `apiCall`: `listMailMailboxes` (the
 * mailbox list) and `listDomains` are the first two calls this surface makes, so a stalled socket left
 * the page itself spinning with no end — the same defect, on the door in front of the one that had been
 * fixed. One constant means a future list read cannot be written with a different budget by accident.
 */
const READ_POLICY: ReadPolicy = { attempts: 3, baseDelayMs: 400, maxDelayMs: 3_000, timeoutMs: 20_000 };

/** Wrap a read in the shared policy, forwarding the caller's signal when it has one. */
function boundedRead<T>(path: string, signal?: AbortSignal): Promise<ApiResponse<T>> {
  return readWithRetry(
    (attemptSignal) => apiCall<T>(path, { signal: signal ?? attemptSignal }),
    READ_POLICY,
  );
}

async function apiCall<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers = await AuthUtils.getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...headers, "Content-Type": "application/json", ...(options.headers || {}) },
  });
  return res.json();
}

/** A real mailbox ON Raisy (InboxKit proxy shape). Full address = username@domain_name. */
export interface Mailbox {
  uid: string;
  username?: string;
  domain_name?: string;
  platform?: string; // GOOGLE | MICROSOFT | AZURE
  status?: string; // granular provisioning status (scheduled → … → active)
  is_admin?: boolean;
}

export function mailboxEmail(m: Mailbox): string {
  if (m.username && m.domain_name) return `${m.username}@${m.domain_name}`;
  return m.uid;
}

/** List the user's real mailboxes ON Raisy (the own-mailbox-on-Raisy surface). */
export async function listMailboxes(): Promise<ApiResponse<{ mailboxes: Mailbox[] }>> {
  // BOUNDED + RETRIED (F11): this is a list read like any other, and it was left unbounded.
  return boundedRead<{ mailboxes: Mailbox[] }>("/api/v1/inboxkit/mailboxes?page=1&limit=100");
}

/**
 * @P080 D-132 — ONE of the sending identity's seven readings.
 *
 * `value` is `null` when nothing backs it, and `source` names what would supply it. That is the whole
 * point of the shape: `0` and "we cannot see it yet" are indistinguishable on a screen, so the BE
 * sends the second as `null` with its reason and this surface never renders it as a number.
 */
export interface MailboxReading {
  reading: string;
  label: string;
  value: number | null;
  measured: boolean;
  /** How many observations the value rests on — 0 when it is not measured. */
  evidence: number;
  /** The evidence in words, e.g. "2 of 3 public checks are in place for this domain". */
  basis: string;
  source: string;
}

/** The lean per-identity contract: seven readings plus ONE line that is deliberately not a score. */
export interface MailboxHealth {
  identity: string | null;
  window_days: number | null;
  readings: MailboxReading[];
  /** How many of the seven could be computed at all — travels with them, never implied. */
  measured_count: number;
  reading_count: number;
  needs_you: string | null;
  limits_source: string;
}

/**
 * What RECEIVERS did with this mailbox's mail — the source that cannot be flattered by our own model
 * (@P080 D-132). Bounded on the BE side, so a slow public lookup returns readings absent rather than
 * holding the drawer.
 */
export async function mailboxHealth(
  uid: string,
  windowDays = 30,
  signal?: AbortSignal,
): Promise<ApiResponse<MailboxHealth>> {
  // ⚠️ BOUNDED AND RETRIED SI CE 2026-10-11 (F12 challenge `F1`). This is the health DRAWER's read, and it
  // was the one read on this surface left on a bare `apiCall` — so a stalled socket still spun the drawer
  // for ever, which is the exact defect the hardening was written to close, one door along.
  //
  // The budget is the read policy: the health payload runs several windowed queries behind one request, so
  // it is given the same three attempts at 20 s as the readiness read rather than a tighter one — a drawer
  // that gives up early reports "did not arrive" about a read that was working.
  const policy: ReadPolicy = { attempts: 3, baseDelayMs: 400, maxDelayMs: 3_000, timeoutMs: 20_000 };
  return readWithRetry(
    (attemptSignal) =>
      apiCall<MailboxHealth>(
        `/api/v1/inboxkit/mailboxes/${encodeURIComponent(uid)}/health?window_days=${windowDays}`,
        { signal: signal ?? attemptSignal },
      ),
    policy,
  );
}

/**
 * @P120 B2 (D-012/D-013/D-014) — WHY this identity is or is not ready, and what it needs.
 *
 * The question the surface answers for the founder: *does this mailbox need warming, and if not,
 * what does it need?* The BE DERIVES the answer from measured signals (`warmup_recommendation_service`)
 * — a calendar is never the reason, and a receiving-only mailbox is `NOT-APPLICABLE`, never `ready`.
 *
 * `headline` is the verdict; `journeys` are the fired signals that produced it, each already carrying
 * its remedy class; `limits` names what could NOT be measured, because an unmeasured signal is not a
 * clean one. `composite` arrives only when the caller asks for `score=1` (it runs the engine battery),
 * and its `engines` are the per-engine sub-scores the hover renders.
 */
export interface WarmupJourney {
  signal: string;
  /** RESOLVE-FIRST · WAIT · WARM · PACED-SEND · FIX-TARGETING · NO-ACTION · NOT-APPLICABLE */
  class: string;
  why: string;
}

export interface WarmupEngineScore {
  engine: string;
  value: number | null;
  measured: boolean;
  normalised?: number | null;
  reason?: string;
}

export interface WarmupComposite {
  scored?: boolean;
  reason?: string;
  engines?: WarmupEngineScore[];
  families?: Record<string, number>;
  family_notes?: Record<string, string>;
  composite?: {
    value: number | null;
    score_100: number | null;
    verdict: string | null;
    method: string;
    families_answered: number;
    families_total: number;
  };
  partial?: boolean;
}

export interface WarmupRecommendation {
  identity: string | null;
  role: string;
  headline: string;
  headline_why: string;
  warmup_required: boolean;
  journeys: WarmupJourney[];
  actions: string[];
  limits: string[];
}

export interface WarmupJourneyPlan {
  identity: string;
  /** not_applicable · not_needed · not_started · warming · at_target · blocked (D-015: the mail cannot leave) */
  state: string;
  message: string;
  why?: string | null;
  today: string[];
  /** D-003's headline: the days until the earliest REAL-VOLUME gate, DERIVED in the BE from the
   *  gate's own counters (never a hardcoded calendar). `null` when the gate could not be read — a
   *  countdown fabricated from nothing is exactly the thing D-003 refuses. */
  countdown?: {
    gates: {
      gate: string;
      days: number | null;
      clears_on: string | null;
      cleared: boolean | null;
      /** `false` means we cannot derive a countdown for this gate at all (the Postmaster tier, until
       *  A5 lands) — reported rather than omitted, because an absent gate would read as "nothing
       *  else to wait for". */
      available?: boolean;
      reason?: string;
      derived_from: string;
    }[];
    earliest: { gate: string; days: number; clears_on: string | null } | null;
    note: string;
  } | null;
  ramp?: {
    day: number | null;
    allowance_today: number | null;
    remaining_today: number | null;
    target_cold_per_day: number | null;
    progress: number | null;
    enforced_by: string;
  };
  exit?: {
    placement_leg: {
      probes: number;
      inbox: number;
      junk: number;
      green: boolean;
      blocking: string[];
      required_probes: number;
      consecutive_clause?: string;
      /** How SPREAD OUT the evidence is. Since D-022 (superseding D-009) the bar REQUIRES a spread,
       *  so these are read by the verdict as well as shown: `distinct_days` below
       *  `required_distinct_days` blocks the leg and says so in `blocking`. The requirement exists
       *  because a same-day burst used to satisfy "stable across probes" exactly (measured
       *  2026-10-06: a restart defect produced a green on seven probes sent in one afternoon).
       *  `null` when the store predates the field — "we cannot say" is not "zero days". */
      required_distinct_days?: number | null;
      /** WHICH JUDGES, and how many. D-009: *"the leg reads 'green on one judge', and every reading
       *  must say so."* D-004's premise is a matrix across every major provider, so a green resting on
       *  one inbox is a different claim from one confirmed across providers. Disclosed BESIDE the
       *  verdict (it does not change `green`), and `null` when the store cannot count them — "we cannot
       *  say" is never rendered as "one", because 1 is exactly the damning value. */
      judges?: string[] | null;
      judge_count?: number | null;
      /** The sentence the BE composes when breadth is known: "green on N judge(s): …". */
      judge_qualification?: string | null;
      distinct_days?: number | null;
      covered_days?: number | null;
      first_probe_at?: string | null;
      last_probe_at?: string | null;
    };
  };
  limits?: string[];
}

export interface WarmupReadiness {
  identity: string;
  domain: string;
  health_score: {
    composite: WarmupComposite | null;
    /** The DOMAIN roll-up (a different object from the identity score — D-014). */
    domain: { sender_trust: number | null; of: number; signals?: unknown } | null;
    identity_measured: {
      placement: Record<string, number> | null;
      auth_as_received: Record<string, { pass: number; fail: number; absent: number }> | null;
      stamped_scores: Record<string, unknown> | null;
      reach: { observable: number; of: number } | null;
    };
  };
  /** The compliance gate's own record — the BE's answer about THIS mailbox, and the ONLY home for the
   *  two fields the panel reads. It was `unknown` until 2026-10-09, which is why the panel re-derived
   *  the fresh-domain date itself and got it wrong. Every future need should read it from here. */
  gate?: {
    /** The date cold outreach opens for this identity, derived by the gate from the mailbox's own
     *  clock (`first_seen` + `fresh_domain.no_sends_before_age_days`). `null` when the gate could not
     *  derive it — show that, never an estimate. */
    cold_sends_open_on?: string | null;
    /** The gate's verdict, which is authoritative even when it named no date. */
    cold_send_allowed_now?: boolean | null;
  } | null;
  recommendation: WarmupRecommendation;
  journey?: WarmupJourneyPlan;
  /** D-003's four KPI groups, and D-002's probe verdict as a card. Both are RE-GROUPINGS of what the
   *  record already carries — nothing is recomputed — and every member names the component that
   *  measured it, so a value can be argued with rather than trusted. */
  kpi_groups?: KpiGroup[];
  /** @P120 C3 — the ladder's decision. Carried by the BE since §29e and, until 2026-10-08, ignored by
   *  every surface: the panel's state is what a founder reads, so the reason a step held has to be here. */
  ladder?: {
    decision?: import("../warmup/ladderDecision").LadderDecision | null;
    /** ⚠️ THE BE'S OWN REASON WHEN IT COULD NOT JUDGE AT ALL (external challenge, 2026-10-09).
     *  `warmup_manager` writes this when the ladder's send window is unreadable — *"a step judged on
     *  zeros would look clean"* — and a test asserts it is produced. Until now NOTHING consumed it, so
     *  the panel fell into its absent-decision branch and printed *"this response carried no ladder
     *  decision"*: a false statement about the response, and the cause (an unreadable ledger) never
     *  reached the person who could fix it. Declared here so it can be shown instead. */
    decision_unavailable?: string | null;
    /** ⚠️ THE SAME DEFECT ONE FIELD OVER, fixed 2026-10-10. `ladder_service.state_for` reports
     *  `readable: false` plus its own `reason` when the ladder STORE cannot be read, and its docstring
     *  states the intent: *"a missing migration can never render as 'the ladder has not started'"*.
     *  The BE honours that; the FE did not — `state` was undeclared and unread, so this case fell into
     *  the absent-decision branch and the operator again read *"this response carried no ladder
     *  decision"*, which is FALSE of a response that carried a ladder block naming its own failure.
     *  Two different causes arrived as one generic sentence, and the fixable one lost its cause. */
    state?: {
      readable?: boolean | null;
      reason?: string | null;
    } | null;
    /** ⚠️ THE GATE THE ADVANCE RESTS ON, OVER THE STEP'S OWN WINDOW (2026-10-12). `decide()` refuses an
     *  advance when placement is unmeasured or sub-100 % — and it reads that over `step_started_at`,
     *  deliberately, so a step cannot advance on evidence about a previous one. The panel meanwhile
     *  showed only the ROLLING 42-day leg (`kpi_groups`, *"inbox placements 13 of 13"`), so **the number
     *  a reader could see was not the number the ladder decides on** and *"will this step certify?"* was
     *  answerable only on the day, from the HOLD's own sentence. Declared here so the step's own leg can
     *  be shown beside the decision. Type mirrors `warmup_manager`'s cell; the window markers travel with
     *  the counts because two legs called placement over two periods is the collision this fixes. */
    placement?: import("../warmup/ladderDecision").StepPlacement | null;
  } | null;
  test_health?: {
    measured: boolean;
    reason?: string;
    at?: string | null;
    outcome?: string | null;
    placement?: string | null;
    found_in?: string | null;
    judge?: string | null;
    readings?: number;
    note?: string;
  };
}

export interface KpiMember {
  name: string;
  value: string | number | boolean | null;
  of?: number | null;
  measured: boolean;
  source: string;
  note?: string | null;
}

export interface KpiGroup {
  group: string;
  members: KpiMember[];
  complete: boolean;
  measured_members: number;
  members_total: number;
}

/**
 * One identity's readiness record. `score` runs the engine battery (the hover sub-scores) and is
 * OFF by default so opening the panel never waits on four containers.
 *
 * @P120 — the panel's production-grade precondition had no timeout and no retry, so a stalled socket
 * left the panel spinning with no end and a transient blip needed a human to reopen the drawer. A READ
 * is idempotent, so it gets a per-attempt timeout and a bounded retry; the bound is ATTEMPTS, and
 * `ReadsExhausted` is thrown so the caller's own catch ends the spinner.
 */
export async function warmupReadiness(
  identity: string,
  role = "sender",
  windowDays = 30,
  score = false,
  signal?: AbortSignal,
): Promise<ApiResponse<WarmupReadiness>> {
  const params = new URLSearchParams({
    identity,
    role,
    window_days: String(windowDays),
    score: score ? "1" : "0",
  });
  const path = `/api/v1/warmup/readiness?${params.toString()}`;
  // Three attempts, ~20 s each, 400 ms → 3 s between them: a blip is absorbed, and a dead backend is
  // reported inside a minute rather than never.
  //
  // The score run gets a single longer budget INSTEAD of a retry, and the number is derived rather than
  // chosen: the four engines run in PARALLEL behind `warmup_scoring_service.ENGINE_TIMEOUT_S` (10 s each,
  // `WARMUP_ENGINE_TIMEOUT_S`), so the leg is bounded near 10–20 s — 60 s leaves 3–6× margin, while a
  // RETRY would re-run four real engine calls (containers, sockets, DNS) on every blip for a number the
  // user only asked for on hover.
  const policy: ReadPolicy = score
    ? { attempts: 1, baseDelayMs: 0, maxDelayMs: 0, timeoutMs: 60_000 }
    : { attempts: 3, baseDelayMs: 400, maxDelayMs: 3_000, timeoutMs: 20_000 };
  // The CALLER's signal (a drawer switch) is chained to each attempt's own abort, so a read abandoned by
  // the user is cancelled even mid-retry (F12 challenge `F2`). `AbortSignal.any` is not used because the
  // estate's browser baseline does not guarantee it — the attempt's signal alone is what releases the socket.
  return readWithRetry((attemptSignal) => apiCall<WarmupReadiness>(path, { signal: signal ?? attemptSignal }), policy);
}

/**
 * @P120 D-002's ACTION — run a TEST HEALTH probe NOW, for one mailbox.
 *
 * D-002: *"Automate TEST HEALTH: one probe fans out … and the system auto-receives + distills the
 * verdict."* The card that REPORTS the last verdict is separate; this is the one that runs a probe,
 * because a founder looking at a week-old reading needs a way to ask *"and now?"*.
 *
 * ⚠️ IT SENDS REAL MAIL from her own mailbox, through the same transport real outreach uses. The BE
 * refuses three ways and each refusal answers with its reason: a receiving-only mailbox is never sent
 * from (D-010), a second probe inside the cooldown returns the verdict we already have, and the wait
 * is bounded. The outcome is C1's vocabulary unchanged — `ran` · `not_seen` · `not_ready` · `error`,
 * plus `refused` and `too_recent` — so `not_seen` reads as the finding it is rather than a failure.
 *
 * ⚠️ NEVER RETRIED, and the timeout is derived from the wait rather than chosen (@P120's hardening).
 * A retry here is a SECOND REAL EMAIL, so this is wrapped in the timeout alone. The BE bounds
 * `wait_seconds` to 5–120 s and then does real work, so the budget is the wait she asked for plus a
 * margin: cutting off a probe that is still running would report a failure for mail that had already
 * gone. If the budget IS spent the caller must say the honest thing — the probe may still have run.
 */
export async function warmupTestHealth(
  identity: string,
  waitSeconds = 30,
): Promise<ApiResponse<Record<string, unknown> & { outcome?: string; reason?: string; test_health?: WarmupReadiness["test_health"] }>> {
  const params = new URLSearchParams({ identity, wait_seconds: String(waitSeconds) });
  const path = `/api/v1/warmup/test-health?${params.toString()}`;
  const budgetMs = Math.max(5, Math.min(120, waitSeconds)) * 1000 + 90_000;
  return withTimeout(
    (signal) => apiCall<Record<string, unknown> & { outcome?: string }>(path, { method: "POST", signal }),
    budgetMs,
  );
}

/**
 * project145 T1329 — the mailbox list the MAIL ENGINE reads (D-133-018).
 *
 * ONE uid space covering both credential owners: the account the user connected themselves
 * (`google:<entity_id>`, read over the Gmail API) and the mailboxes Raisy provisions (read over
 * IMAP). `readable: false` plus a `reason` means the mailbox exists and this surface cannot open it
 * yet — it is SHOWN, never hidden, so the list never looks emptier than it is (FTUE implication 11,
 * channel-complete), and it is never rendered as an empty folder.
 */
export interface MailMailbox {
  uid: string;
  address: string | null;
  /** google | microsoft | azure — the mailbox's own platform, never assumed. */
  provider: string | null;
  readable: boolean;
  reason: string | null;
  status?: string | null;
  /**
   * True when NO workspace owns this mailbox yet — Raisy provisioned it, but it has not been linked
   * to a workspace. It is shared infrastructure of the deployment rather than this workspace's own
   * sending identity, so the surface says so instead of implying ownership (D-133-021).
   */
  shared?: boolean;
}

export function mailMailboxAddress(m: MailMailbox): string {
  return m.address ?? m.uid;
}

/**
 * The entity-scoped row, in the shape the own-mailbox screens already render (P080 T0127 · §D-133).
 *
 * WHY AN ADAPTER RATHER THAN A CAST. The two routes answer with DIFFERENT FACTS: `/mail/mailboxes`
 * returns the mailbox's full ADDRESS plus whether we can open it (`readable` / `reason`) and whether
 * any entity owns it (`shared`), while `/inboxkit/mailboxes` splits the address into `username` +
 * `domain_name`. Splitting it back apart here keeps every existing renderer working while the data
 * comes from the route that enforces entity access — and it keeps that difference visible instead of
 * hiding it behind a cast that would compile and then render `undefined`.
 *
 * A row with NO address keeps its `uid` as the display value — which is what a person-connected
 * account (`google:<entity_id>`) has, and `mailboxEmail` already falls back to the uid for that case.
 */
export function mailMailboxRow(m: MailMailbox): Mailbox {
  const address = m.address ?? "";
  const at = address.lastIndexOf("@");
  return {
    uid: m.uid,
    username: at > 0 ? address.slice(0, at) : undefined,
    domain_name: at > 0 ? address.slice(at + 1) : undefined,
    platform: m.provider ?? undefined,
    status: m.status ?? undefined,
  };
}

export async function listMailMailboxes(
  entityId: string,
): Promise<ApiResponse<{ mailboxes: MailMailbox[] }>> {
  // BOUNDED + RETRIED (F11) — THE PAGE'S PRIMARY LOAD. Unbounded here meant the surface a user opens
  // first could spin for ever, which is the defect the hardening exists to close.
  return boundedRead<{ mailboxes: MailMailbox[] }>(
    `/api/v1/mail/mailboxes?entity_id=${encodeURIComponent(entityId)}`,
  );
}

/**
 * Button 2 — "Send test email to…" (P080 D-102): a REAL send FROM the user's Raisy mailbox
 * (fromAddress) TO a target they chose (toEmail, typically their own external inbox).
 * test_kind="external_visibility" makes the BE stamp {test:true, kind} — never a reachout.
 * entityId is required (the owning entity the communication is attributed to).
 */
export interface SendTestEmailInput {
  toEmail: string;
  fromAddress: string; // the user's Raisy sending mailbox (full address)
  entityId: string; // owning entity for the communication row
  ownerEntityId?: string;
  subject?: string;
  bodyText?: string;
}

export async function sendTestEmail(
  input: SendTestEmailInput,
): Promise<ApiResponse<{ communication_id?: string; status?: string }>> {
  // ⚠️ TIMEOUT ONLY, NEVER A RETRY — F12 challenge `F3`, and the record's own rule applied to the button
  // the record had not looked at. `warmupTestHealth` was bounded and this one was not, so "the SEND takes
  // the timeout alone" was true of one of the two sends on the page: a hung socket on *Send test email to…*
  // left its modal spinning for ever with `confirmLoading` true.
  //
  // The budget is generous on purpose (150 s): this call hands a message to the provider, and cutting it off
  // early would report a failure for mail that had already gone — the same reason `warmupTestHealth` sizes
  // its budget from the wait the user asked for. A timeout here means "we stopped waiting", never "it did
  // not send", and the caller's note is written to say exactly that.
  return withTimeout((signal) => apiCall("/api/v1/email/send", {
    method: "POST",
    signal,
    body: JSON.stringify({
      to_email: input.toEmail,
      from_address: input.fromAddress,
      reply_to: input.fromAddress,
      subject: input.subject ?? "Raisy test email",
      body_text:
        input.bodyText ??
        "This is a test email sent from your Raisy mailbox to confirm your setup works.",
      entity_id: input.entityId,
      owner_entity_id: input.ownerEntityId,
      content_class: "transactional",
      test_kind: "external_visibility",
    }),
  }), SEND_TIMEOUT_MS);
}

/** A real domain ON Raisy (InboxKit proxy shape). Its DNS records carry the auth/health truth. */
export interface RaisyDomain {
  uid: string;
  name: string;
  tld?: string;
  status?: string;
  connection_type?: string;
  dns_propagation_status?: string;
  nameserver_match_status?: string;
  assigned_mailboxes?: number;
  createdAt?: string;
  dns?: {
    records?: Array<{
      host?: string;
      type?: string;
      value?: string;
      status?: string;
    }>;
  };
}

/** List the user's real domains (InboxKit proxy) — source of DNS auth + domain-age health. */
export async function listDomains(): Promise<ApiResponse<{ domains: RaisyDomain[] }>> {
  // BOUNDED + RETRIED (F11): the page's other first-load call.
  return boundedRead<{ domains: RaisyDomain[] }>("/api/v1/inboxkit/domains?page=1&limit=100");
}

// ⚠️ REMOVED 2026-10-09 (@P292 T2922): `fresh15Target()` DERIVED the fresh-domain date in the
// CLIENT — a hardcoded **15** days added to InboxKit's `domain.createdAt` — while the rule is
// `fresh_domain.no_sends_before_age_days = 14` and the gate anchors on the mailbox's own
// `first_seen`. The readiness panel used it and could tell a founder "real investor volume is
// safe" about a domain the gate was still REFUSING (§29ae). It is deleted rather than deprecated
// because nothing called it once the panel read the BE's own answer, and a duration helper left
// lying around is exactly what @P292 exists to eliminate — the next surface would have adopted it.
// Anything needing this date reads `gate.cold_sends_open_on` / `gate.cold_send_allowed_now`.

export interface DomainAuthCheck {
  mx: boolean;
  spf: boolean;
  dkim: boolean;
  dmarc: boolean;
}

/** Derive the real auth posture from the domain's actual DNS records (never fabricated). */
export function domainAuthCheck(domain: RaisyDomain | undefined): DomainAuthCheck {
  const records = domain?.dns?.records ?? [];
  const byType = (t: string) => records.filter((r) => (r.type || "").toUpperCase() === t);
  const mx = byType("MX").length > 0;
  const spf = byType("TXT").some((r) => (r.value || "").toLowerCase().includes("v=spf1"));
  const dkim = byType("TXT").some((r) => (r.host || "").toLowerCase().includes("_domainkey"));
  const dmarc = byType("TXT").some((r) => (r.host || "").toLowerCase().includes("_dmarc"));
  return { mx, spf, dkim, dmarc };
}


